
import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Flame, AlertTriangle, ExternalLink } from 'lucide-react';
import type { FireDangerStatus } from '../../services/fireDangerService';
import { getHazardIcon, getHazardI18nKey } from './hazardIcon';
import WarningAreaMiniMap from './WarningAreaMiniMap';

interface FireDangerModalProps {
  status: FireDangerStatus;
  onClose: () => void;
}

const SEVERITY_STYLES: Record<string, string> = {
  Yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Orange: 'bg-orange-100 text-orange-800 border-orange-300',
  Red: 'bg-red-100 text-red-800 border-red-300',
};

const FireDangerModal: React.FC<FireDangerModalProps> = ({ status, onClose }) => {
  const { t } = useTranslation();
  const hasActiveWarnings = status.isInDangerZone && status.activeWarnings.length > 0;

  return (
    <div
      className="fixed inset-0 z-[10004] flex items-end justify-center sm:items-center animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-[var(--bg-surface)] rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300 max-h-[90dvh] overflow-y-auto"
        style={{ overscrollBehavior: 'contain', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle bar (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[var(--border-secondary)]" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X size={18} />
        </button>

        <div className="px-6 pt-5 pb-6">
          {/* Header */}
          {hasActiveWarnings ? (
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
                status.highestSeverity === 'Red' ? 'bg-red-100' : 'bg-orange-100'
              }`}>
                <AlertTriangle size={22} className={
                  status.highestSeverity === 'Red' ? 'text-red-600' : 'text-orange-600'
                } />
              </div>
              <div>
                <h2 className="text-lg font-black text-[var(--text-primary)] leading-tight">
                  {status.dominantHazard && status.dominantHazard !== 'forestFire'
                    ? t(`fire_danger.active_title.${status.dominantHazard}`)
                    : t('fire_danger.active_warning_title')}
                </h2>
                <p className="text-tiny text-[var(--text-muted)] mt-0.5">
                  {status.activeWarnings[0]?.source === 'smhi'
                    ? t('fire_danger.source_smhi')
                    : status.dominantHazard && status.dominantHazard !== 'forestFire'
                      ? t('fire_danger.source_varsom')
                      : t('fire_danger.source_met')}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center">
                <Flame size={22} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-black text-[var(--text-primary)] leading-tight">
                  {t('fire_danger.general_ban_title')}
                </h2>
                <p className="text-tiny text-[var(--text-muted)] mt-0.5">
                  15. {t('fire_danger.april')} – 15. {t('fire_danger.september')}
                </p>
              </div>
            </div>
          )}

          {/* Active warnings list */}
          {hasActiveWarnings && (
            <div className="space-y-3 mb-5">
              <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
                {status.dominantHazard && status.dominantHazard !== 'forestFire'
                  ? t('fire_danger.active_warning_body_generic')
                  : t('fire_danger.active_warning_body')}
              </p>

              {status.activeWarnings.map((w, i) => {
                const HazardIcon = getHazardIcon(w.hazardType);
                return (
                  <div
                    key={i}
                    className={`rounded-2xl border p-4 ${SEVERITY_STYLES[w.severity] || SEVERITY_STYLES.Yellow}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <HazardIcon size={14} />
                      <span className="text-tiny font-black uppercase tracking-wider">
                        {t(getHazardI18nKey(w.hazardType))} · {w.severity}
                      </span>
                    </div>
                    {w.area && (
                      <p className="text-[12px] font-bold mb-1">
                        {t('fire_danger.area_label')}: {w.area}
                      </p>
                    )}
                    {w.polygon && (
                      <WarningAreaMiniMap
                        polygon={w.polygon}
                        severity={w.severity}
                        center={status.center}
                        areaName={w.area}
                      />
                    )}
                    {w.description && (
                      <p className="text-tiny leading-relaxed opacity-80">
                        {w.description}
                      </p>
                    )}
                    {w.instruction && (
                      <p className="text-tiny leading-relaxed mt-1 font-medium">
                        {w.instruction}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* General ban info (primary if no active warnings, secondary if combined) */}
          {status.isGeneralBanActive && (
            <div className={hasActiveWarnings ? 'mt-4 pt-4 border-t border-[var(--border-secondary)]' : ''}>
              {hasActiveWarnings && (
                <p className="text-tiny font-bold text-amber-700 mb-2 flex items-center gap-1.5">
                  <Flame size={13} />
                  {t('fire_danger.also_general_ban')}
                </p>
              )}
              <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
                {t('fire_danger.general_ban_body')}
              </p>
              <p className="text-[12px] text-[var(--text-muted)] leading-relaxed mt-2 italic">
                {t('fire_danger.general_ban_exceptions')}
              </p>
            </div>
          )}

          {/* Safety tips */}
          {(() => {
            const hasFireContext = status.isGeneralBanActive || status.activeWarnings.some(w => w.hazardType === 'forestFire');
            const nonFireHazards: ('avalanche' | 'flood' | 'landslide')[] = [];
            for (const w of status.activeWarnings) {
              if (w.hazardType !== 'forestFire' && !nonFireHazards.includes(w.hazardType as any)) {
                nonFireHazards.push(w.hazardType as 'avalanche' | 'flood' | 'landslide');
              }
            }
            return (
              <div className="mt-5 pt-4 border-t border-[var(--border-secondary)]">
                <p className="text-tiny font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">
                  {t('fire_danger.safety_tips_title')}
                </p>
                <ul className="space-y-1.5">
                  {hasFireContext && [1, 2, 3].map(n => (
                    <li key={`fire-${n}`} className="text-[12px] text-[var(--text-secondary)] leading-relaxed flex gap-2">
                      <span className="text-red-400 mt-0.5 shrink-0">&#8226;</span>
                      {t(`fire_danger.safety_tip_${n}`)}
                    </li>
                  ))}
                  {nonFireHazards.map(h => (
                    <li key={`tip-${h}`} className="text-[12px] text-[var(--text-secondary)] leading-relaxed flex gap-2">
                      <span className="text-red-400 mt-0.5 shrink-0">&#8226;</span>
                      {t(`fire_danger.hazard_tip.${h}`)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Sources — vis kun kilder som er relevante for de aktive varslene.
              Norske kilder (met.no, lovdata) vises kun ved norske varsler eller norsk
              generelt bålforbud. SMHI vises kun ved svenske varsler. */}
          {(() => {
            const hasMetFire = status.activeWarnings.some(w => w.source === 'met' && w.hazardType === 'forestFire');
            const hasSmhiFire = status.activeWarnings.some(w => w.source === 'smhi' && w.hazardType === 'forestFire');
            const hasSmhiOther = status.activeWarnings.some(w => w.source === 'smhi' && w.hazardType !== 'forestFire');
            const hasMetAvalanche = status.activeWarnings.some(w => w.source === 'met' && w.hazardType === 'avalanche');
            const hasMetFloodOrLandslide = status.activeWarnings.some(w => w.source === 'met' && (w.hazardType === 'flood' || w.hazardType === 'landslide'));
            const showMet = hasMetFire || (status.isGeneralBanActive && !hasSmhiFire);
            return (
              <div className="mt-5 pt-4 border-t border-[var(--border-secondary)]">
                <p className="text-tiny font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">
                  {t('fire_danger.sources_title')}
                </p>
                <div className="space-y-2">
                  {showMet && (
                    <a
                      href="https://www.met.no/vaer-og-klima/farevarsel"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-tiny text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      <ExternalLink size={11} className="shrink-0 text-[var(--text-muted)]" />
                      <span>{t('fire_danger.source_met_detail')}</span>
                    </a>
                  )}
                  {hasMetAvalanche && (
                    <a
                      href="https://www.varsom.no/snoskredvarsling/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-tiny text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      <ExternalLink size={11} className="shrink-0 text-[var(--text-muted)]" />
                      <span>{t('fire_danger.source_varsom_avalanche_detail')}</span>
                    </a>
                  )}
                  {hasMetFloodOrLandslide && (
                    <a
                      href="https://www.varsom.no/flom-og-jordskredvarsling/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-tiny text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      <ExternalLink size={11} className="shrink-0 text-[var(--text-muted)]" />
                      <span>{t('fire_danger.source_varsom_flood_detail')}</span>
                    </a>
                  )}
                  {hasSmhiFire && (
                    <a
                      href="https://www.smhi.se/vader/varningar-och-brandrisk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-tiny text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      <ExternalLink size={11} className="shrink-0 text-[var(--text-muted)]" />
                      <span>{t('fire_danger.source_smhi_detail')}</span>
                    </a>
                  )}
                  {hasSmhiOther && (
                    <a
                      href="https://www.smhi.se/vader/varningar-och-prognoser/varningar-och-meddelanden/varningar"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-tiny text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      <ExternalLink size={11} className="shrink-0 text-[var(--text-muted)]" />
                      <span>{t('fire_danger.source_smhi_hydro_detail')}</span>
                    </a>
                  )}
                  {status.isGeneralBanActive && (
                    <a
                      href="https://lovdata.no/dokument/SF/forskrift/2002-06-26-847"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-tiny text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      <ExternalLink size={11} className="shrink-0 text-[var(--text-muted)]" />
                      <span>{t('fire_danger.source_law_detail')}</span>
                    </a>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default FireDangerModal;
